import { useEffect, useMemo, useState } from 'react';

export interface Countdown {
  h: number;
  m: number;
  s: number;
  expired: boolean;
}

export type CountdownInstant = Date | number;

const ZERO: Countdown = { h: 0, m: 0, s: 0, expired: true };

const toMs = (instant: CountdownInstant | undefined): number =>
  instant === undefined ? Number.NaN : instant instanceof Date ? instant.getTime() : instant;

/** Splits a remaining duration into whole hours, minutes and seconds, never below zero or NaN. */
export const toCountdown = (remainingMs: number): Countdown => {
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return ZERO;

  // Round up, so the display reads 0s only once the target has actually passed.
  const total = Math.ceil(remainingMs / 1000);
  return {
    h: Math.floor(total / 3600),
    m: Math.floor(total / 60) % 60,
    s: total % 60,
    expired: false,
  };
};

/** Formats a countdown as `{h}h {mm}m {ss}s`. */
export const formatCountdown = ({ h, m, s }: Countdown): string =>
  `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;

const isHidden = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Counts down to `endsAt`, ticking once a second.
 *
 * Every tick recomputes from the clock rather than decrementing, so a throttled or backgrounded timer
 * can't drift. `now` is the server's time when `endsAt` was issued: the gap between it and the client
 * clock is applied to every tick, so a wrong device clock still counts down to the server's reset.
 *
 * Ticking stops while the document is hidden and resyncs the moment it is visible again, stops once
 * expired, and is cleaned up on unmount. A missing or invalid `endsAt` reads as expired.
 */
export const useCountdown = (endsAt: CountdownInstant | undefined, now?: CountdownInstant): Countdown => {
  const endsAtMs = toMs(endsAt);
  const serverNowMs = toMs(now);

  // Server minus client, captured when the server time arrives.
  const skewMs = useMemo(() => (Number.isFinite(serverNowMs) ? serverNowMs - Date.now() : 0), [serverNowMs]);

  const [clientNowMs, setClientNowMs] = useState(() => Date.now());

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stop = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };

    const tick = () => {
      stop();
      const current = Date.now();
      setClientNowMs(current);

      const remaining = endsAtMs - (current + skewMs);
      if (!Number.isFinite(remaining) || remaining <= 0 || isHidden()) return;

      // Wake when the displayed second changes, rather than on a free-running interval.
      timer = setTimeout(tick, remaining % 1000 || 1000);
    };

    const onVisibilityChange = () => (isHidden() ? stop() : tick());

    tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [endsAtMs, skewMs]);

  return toCountdown(endsAtMs - (clientNowMs + skewMs));
};
